import { useEffect, useRef, useState } from "react";
import { Send, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { ConfirmDelete } from "./ConfirmDelete";

export const AdminChats = ({ onChange }) => {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [chat, setChat] = useState(null);
  const [text, setText] = useState("");
  const end = useRef();
  const loadList = () => api.get("/admin/chats").then((r) => setList(r.data)).catch(() => {});
  const loadChat = (id) => api.get(`/admin/chats/${id}`).then((r) => setChat(r.data)).catch(() => {});
  useEffect(() => { loadList(); const t = setInterval(loadList, 6000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!sel) return undefined;
    loadChat(sel); onChange?.();
    const t = setInterval(() => loadChat(sel), 5000);
    return () => clearInterval(t);
  }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [chat?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/admin/chats/${sel}/messages`, { text }); setText(""); loadChat(sel); loadList(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const remove = async () => { await api.delete(`/admin/chats/${sel}`); setSel(null); setChat(null); loadList(); onChange?.(); toast.success("Chat eliminata"); };

  return (
    <div data-testid="admin-chats">
      <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-6">Chat con i visitatori</h2>
      <div className="grid md:grid-cols-[280px_1fr] gap-4 min-h-[480px]">
        <div className="space-y-2 max-h-[560px] overflow-y-auto" data-testid="admin-chat-list">
          {!list.length && <p className="text-sm text-zinc-400 border border-dashed border-white/15 p-6 text-center">Nessuna chat ancora.</p>}
          {list.map((c) => (
            <button key={c.id} onClick={() => setSel(c.id)} data-testid={`admin-chat-item-${c.id}`}
              className={`w-full text-left rounded-xl border p-3 transition-colors ${sel === c.id ? "border-emerald-400 bg-emerald-400/10" : "border-white/10 bg-[#1e2236] hover:border-white/30"}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold truncate">{c.name}</p>
                {c.unread_admin > 0 && <span className="h-5 min-w-5 px-1.5 rounded-full bg-hot text-[#151827] text-[10px] font-bold grid place-items-center">{c.unread_admin}</span>}
              </div>
              <p className="text-xs text-zinc-400 truncate mt-0.5">{c.last_text}</p>
            </button>
          ))}
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#1e2236] flex flex-col min-h-[480px]">
          {!chat ? <p className="m-auto text-sm text-zinc-400">Seleziona una chat per leggere e rispondere.</p> : (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div>
                  <p className="font-semibold" data-testid="admin-chat-name">{chat.name}</p>
                  {chat.email && <a href={`mailto:${chat.email}`} className="text-xs text-zinc-400 hover:text-emerald-400 inline-flex items-center gap-1"><Mail className="h-3 w-3" />{chat.email}</a>}
                </div>
                <ConfirmDelete onConfirm={remove} testid="admin-chat-delete" title="Eliminare questa chat?"><Trash2 className="h-4 w-4" /></ConfirmDelete>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[420px]" data-testid="admin-chat-messages">
                {chat.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-line ${m.sender === "admin" ? "bg-emerald-500 text-[#151827]" : "bg-[#2c3149]"}`}>
                      {m.text}<p className="text-[10px] mt-1 opacity-60">{new Date(m.created_at).toLocaleString("it-IT")}</p>
                    </div>
                  </div>
                ))}
                <div ref={end} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
                <input value={text} maxLength={2000} onChange={(e) => setText(e.target.value)} placeholder="Rispondi…" className="flex-1 rounded-full bg-[#262b42] border border-white/15 px-4 text-sm outline-none focus:border-emerald-400" data-testid="admin-chat-input" />
                <button className="h-10 w-10 rounded-full bg-emerald-500 text-[#151827] grid place-items-center" aria-label="Invia" data-testid="admin-chat-send-btn"><Send className="h-4 w-4" /></button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
